import React, { useState, useEffect, useCallback } from 'react';
import { categoriasAPI, chavesAPI, inscricoesAPI } from '../services/api';
import { CampeonatosService } from '../services/campeonatosService';
import './GerarChaves.css';

function GerarChaves() {
  const [campeonatos, setCampeonatos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [chavesGeradas, setChavesGeradas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedCampeonato, setSelectedCampeonato] = useState('');
  const [selectedCategoria, setSelectedCategoria] = useState('');
  const [selectedFaixa, setSelectedFaixa] = useState('');
  const [faixasDisponiveis, setFaixasDisponiveis] = useState([]);
  const [inscritos, setInscritos] = useState([]);
  const [loadingInscritos, setLoadingInscritos] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (!selectedCampeonato) {
      setInscritos([]);
      return;
    }
    setLoadingInscritos(true);
    inscricoesAPI.listarPorCampeonato(selectedCampeonato)
      .then(({ data }) => setInscritos(data.filter((i) => i.status === 'confirmada')))
      .catch(() => setInscritos([]))
      .finally(() => setLoadingInscritos(false));
  }, [selectedCampeonato]);

  useEffect(() => {
    if (selectedCategoria) {
      const categoria = categorias.find(c => c.id === selectedCategoria);
      setFaixasDisponiveis(categoria ? categoria.faixas : []);
      setSelectedFaixa('');
    } else {
      setFaixasDisponiveis([]);
    }
  }, [selectedCategoria, categorias]);

  const loadData = async () => {
    try {
      const [campeonatosRes, categoriasRes, chavesRes] = await Promise.all([
        CampeonatosService.listar(),
        categoriasAPI.getAll(),
        chavesAPI.getAll()
      ]);
      setCampeonatos(campeonatosRes);
      setCategorias(categoriasRes.data);
      setChavesGeradas(chavesRes.data);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  // Inscritos confirmados que batem com a categoria/faixa selecionadas — é
  // exatamente o mesmo critério que o backend usa em POST /chaves/gerar.
  const getAtletasDisponiveis = useCallback(() => {
    if (!selectedCategoria) return [];
    return inscritos
      .filter((i) => (i.categorias || []).some((c) => c.categoria_id === selectedCategoria))
      .filter((i) => !selectedFaixa || i.faixa === selectedFaixa)
      .map((i) => ({ id: i.aluno_id, nome: i.aluno_nome, faixa: i.faixa }));
  }, [inscritos, selectedCategoria, selectedFaixa]);

  const alunosDisponiveis = getAtletasDisponiveis();

  const getTamanhoChave = (numAtletas) => {
    if (numAtletas >= 3 && numAtletas <= 4) return 4;
    if (numAtletas >= 5 && numAtletas <= 8) return 8;
    return 16;
  };

  const getNumeroPaginas = (numAtletas) => Math.ceil(numAtletas / 16);

  const chavesDoCampeonato = chavesGeradas.filter((ch) => ch.campeonato_id === selectedCampeonato);

  const chaveJaGerada = (categoriaId, faixa) => {
    return chavesDoCampeonato.some(chave =>
      chave.categoria_id === categoriaId &&
      (faixa ? chave.faixa === faixa : !chave.faixa)
    );
  };

  const getCategoriasComChaves = () => {
    const set = new Set();
    chavesDoCampeonato.forEach(chave => {
      const key = chave.faixa ? `${chave.categoria_id}-${chave.faixa}` : chave.categoria_id;
      set.add(key);
    });
    return set;
  };

  const getTotalCombinacoes = () => {
    let total = 0;
    categorias.forEach(cat => {
      total += (cat.faixas && cat.faixas.length > 0) ? cat.faixas.length : 1;
    });
    return total;
  };

  const chavesJaGeradasCount = getCategoriasComChaves().size;
  const totalPossivel = getTotalCombinacoes();
  const chavesPendentes = totalPossivel - chavesJaGeradasCount;

  const handleGerar = async () => {
    if (!selectedCampeonato) {
      setMessage({ type: 'error', text: '⚠️ Selecione um campeonato para continuar.' });
      return;
    }
    if (!selectedCategoria) {
      setMessage({ type: 'error', text: '⚠️ Categoria Não Selecionada - Por favor, selecione uma categoria para continuar.' });
      return;
    }

    if (chaveJaGerada(selectedCategoria, selectedFaixa)) {
      setMessage({
        type: 'error',
        text: `⚠️ Chave Já Gerada - Esta combinação de categoria${selectedFaixa ? ' e faixa' : ''} já possui chave gerada para este campeonato.`
      });
      return;
    }

    if (alunosDisponiveis.length < 3) {
      setMessage({
        type: 'error',
        text: `❌ Número Insuficiente de Atletas - São necessários no mínimo 3 atletas com inscrição confirmada. Atualmente há apenas ${alunosDisponiveis.length}.`
      });
      return;
    }

    setGenerating(true);
    setMessage(null);

    try {
      await chavesAPI.gerar({
        campeonatoId: selectedCampeonato,
        categoriaId: selectedCategoria,
        faixa: selectedFaixa || undefined
      });

      const categoria = categorias.find(c => c.id === selectedCategoria);
      const numPaginas = getNumeroPaginas(alunosDisponiveis.length);
      setMessage({
        type: 'success',
        text: `✅ Chave Gerada com Sucesso! - Chave criada para a categoria "${categoria?.nome}"${numPaginas > 1 ? ` com ${numPaginas} páginas` : ''}. Você será redirecionado em instantes...`
      });

      setTimeout(() => {
        window.location.href = '/chaves';
      }, 2000);
    } catch (error) {
      console.error('Erro ao gerar chaves:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.error || '❌ Erro ao Gerar Chaves - Não foi possível gerar as chaves. Verifique sua conexão e tente novamente.'
      });
    } finally {
      setGenerating(false);
    }
  };

  if (loading) return <div className="loading">Carregando...</div>;

  const campeonato = campeonatos.find(c => c.id === selectedCampeonato);
  const categoria = categorias.find(c => c.id === selectedCategoria);
  const tamanhoChave = getTamanhoChave(alunosDisponiveis.length);
  const numeroPaginas = getNumeroPaginas(alunosDisponiveis.length);
  const jaTemChave = chaveJaGerada(selectedCategoria, selectedFaixa);

  return (
    <div>
      <div className="page-header">
        <h1>Geração de Chaves</h1>
        <p>Gere chaves automaticamente a partir dos inscritos confirmados de um campeonato</p>
      </div>

      {selectedCampeonato && (
        <div className="card" style={{ marginBottom: '20px', background: '#e3f2fd' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ margin: 0, color: '#1976d2' }}>📊 Status de Geração — {campeonato?.nome}</h3>
              <p style={{ margin: '5px 0 0 0', color: '#666' }}>Acompanhe o progresso das chaves deste campeonato</p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#1976d2' }}>{chavesJaGeradasCount}/{totalPossivel}</div>
              <div style={{ fontSize: '14px', color: '#666' }}>
                {chavesPendentes > 0 ? `${chavesPendentes} pendente${chavesPendentes !== 1 ? 's' : ''}` : 'Todas geradas! ✅'}
              </div>
            </div>
          </div>
        </div>
      )}

      {message && (
        <div className={message.type === 'error' ? 'error' : 'success'}>
          {message.text}
        </div>
      )}

      <div className="gerar-container">
        <div className="form-section">
          <div className="card">
            <h3>Configurações</h3>

            <div className="form-group">
              <label>Campeonato *</label>
              <select
                value={selectedCampeonato}
                onChange={(e) => { setSelectedCampeonato(e.target.value); setSelectedCategoria(''); }}
                disabled={generating}
              >
                <option value="">Selecione um campeonato</option>
                {campeonatos.map(camp => (
                  <option key={camp.id} value={camp.id}>{camp.nome}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Categoria *</label>
              <select
                value={selectedCategoria}
                onChange={(e) => setSelectedCategoria(e.target.value)}
                disabled={generating || !selectedCampeonato}
              >
                <option value="">Selecione uma categoria</option>
                {categorias.map(cat => {
                  const temChave = chavesDoCampeonato.some(ch => ch.categoria_id === cat.id);
                  return (
                    <option key={cat.id} value={cat.id}>
                      {cat.nome} {temChave ? '✓' : ''}
                    </option>
                  );
                })}
              </select>
            </div>

            {faixasDisponiveis.length > 0 && (
              <div className="form-group">
                <label>Faixa (opcional)</label>
                <select
                  value={selectedFaixa}
                  onChange={(e) => setSelectedFaixa(e.target.value)}
                  disabled={generating}
                >
                  <option value="">Todas as faixas</option>
                  {faixasDisponiveis.map(faixa => {
                    const faixaJaGerada = chaveJaGerada(selectedCategoria, faixa);
                    return (
                      <option key={faixa} value={faixa} disabled={faixaJaGerada}>
                        {faixa} {faixaJaGerada ? '✓ (já gerada)' : ''}
                      </option>
                    );
                  })}
                </select>
              </div>
            )}

            {jaTemChave && (
              <div className="warning-box" style={{ background: '#fff3cd', border: '1px solid #ffc107', padding: '10px', borderRadius: '4px', marginBottom: '10px' }}>
                ⚠️ Esta combinação já possui chave gerada
              </div>
            )}

            <button
              className="btn btn-success"
              onClick={handleGerar}
              disabled={generating || !selectedCampeonato || alunosDisponiveis.length < 3 || jaTemChave}
              style={{ width: '100%', marginTop: '20px' }}
            >
              {generating ? 'Gerando...' : jaTemChave ? 'Chave Já Gerada' : 'Gerar Chaves'}
            </button>
          </div>
        </div>

        <div className="info-section">
          <div className="card">
            <h3>Informações</h3>

            {!selectedCampeonato && (
              <p style={{ color: '#999', textAlign: 'center' }}>Selecione um campeonato para começar</p>
            )}

            {selectedCampeonato && !selectedCategoria && (
              <p style={{ color: '#999', textAlign: 'center' }}>Selecione uma categoria para ver as informações</p>
            )}

            {selectedCategoria && (
              <>
                <div className="info-item">
                  <span className="info-label">Categoria:</span>
                  <span className="info-value">{categoria?.nome}</span>
                </div>

                {selectedFaixa && (
                  <div className="info-item">
                    <span className="info-label">Faixa:</span>
                    <span className="info-value">{selectedFaixa}</span>
                  </div>
                )}

                <div className="info-item">
                  <span className="info-label">Inscritos confirmados:</span>
                  <span className="info-value">{loadingInscritos ? '…' : alunosDisponiveis.length}</span>
                </div>

                {alunosDisponiveis.length >= 3 && (
                  <>
                    <div className="info-item">
                      <span className="info-label">Tipo de chave:</span>
                      <span className="info-value">Chave de {tamanhoChave}</span>
                    </div>

                    {numeroPaginas > 1 && (
                      <div className="info-item">
                        <span className="info-label">Número de páginas:</span>
                        <span className="info-value">{numeroPaginas}</span>
                      </div>
                    )}
                  </>
                )}

                {!loadingInscritos && alunosDisponiveis.length < 3 && (
                  <div className="warning-box">
                    Mínimo de 3 atletas com inscrição confirmada nesse campeonato/categoria
                  </div>
                )}
              </>
            )}
          </div>

          {alunosDisponiveis.length > 0 && (
            <div className="card">
              <h3>Atletas ({alunosDisponiveis.length})</h3>
              <div className="atletas-list">
                {alunosDisponiveis.map(aluno => (
                  <div key={aluno.id} className="atleta-item">
                    <span className="atleta-nome">{aluno.nome}</span>
                    <span className="atleta-faixa">{aluno.faixa}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="card" style={{ marginTop: '30px' }}>
        <h3>Como funciona?</h3>
        <div className="regras">
          <div className="regra-item">
            <strong>Fonte dos atletas:</strong> apenas inscrições confirmadas (pagamento validado pelo organizador) daquele campeonato específico entram na chave
          </div>
          <div className="regra-item">
            <strong>3 a 4 atletas:</strong> Será gerada uma chave de 4
          </div>
          <div className="regra-item">
            <strong>5 a 8 atletas:</strong> Será gerada uma chave de 8
          </div>
          <div className="regra-item">
            <strong>9 a 16 atletas:</strong> Será gerada uma chave de 16
          </div>
          <div className="regra-item">
            <strong>Sorteio:</strong> Os atletas serão sorteados aleatoriamente nas posições da chave
          </div>
        </div>
      </div>

      {chavesDoCampeonato.length > 0 && (
        <div className="card" style={{ marginTop: '20px' }}>
          <h3>✓ Chaves já geradas neste campeonato ({chavesDoCampeonato.length})</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '10px', marginTop: '15px' }}>
            {chavesDoCampeonato.map(chave => {
              const cat = categorias.find(c => c.id === chave.categoria_id);
              return (
                <div key={chave.id} style={{
                  padding: '10px',
                  background: '#f0f0f0',
                  borderRadius: '5px',
                  border: '1px solid #ddd'
                }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>{cat?.nome || 'Categoria'}</div>
                  {chave.faixa && <div style={{ fontSize: '14px', color: '#666' }}>Faixa: {chave.faixa}</div>}
                  <div style={{ fontSize: '12px', color: '#999', marginTop: '5px' }}>
                    {chave.num_atletas} atletas
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default GerarChaves;
